import { useState, useEffect } from "react"
import { motion, AnimatePresence } from "framer-motion"
import {ArrowRight, Truck, Heart, Menu, X} from 'lucide-react'
import { Link } from "react-router-dom"
import HavenNav from "@/components/HavenNav"

const JANE_APP = 'https://havenintegrativehealth.janeapp.com/login'

const acneNavLinks = [
  { label: 'Home', href: '/' },
  { label: 'Acne Care', href: '#acne-care' },
  { label: 'Treatments', href: '#treatments' },
  { label: 'How It Works', href: '#how-it-works' },
  { label: 'Why Haven', href: '#why-haven' },
  { label: 'FAQ', href: '#faq' },
]

function AcneSubNav() {
  const [open, setOpen] = useState(false)
  const [scrolled, setScrolled] = useState(false)

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 80)
    window.addEventListener('scroll', onScroll)
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  return (
    <div className={`fixed top-[76px] left-0 right-0 z-40 transition-all duration-500 ${scrolled ? 'bg-[#FAF8F6]/95 backdrop-blur-md shadow-[0_1px_0_rgba(201,112,112,0.1)]' : 'bg-[#FAF8F6]/70 backdrop-blur-sm'}`}>
      <div className="max-w-[1440px] mx-auto px-5 sm:px-8 lg:px-12 py-3.5 flex items-center justify-between">
        <a href="#top" className="flex items-center gap-2.5 group">
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#C97070] to-[#9B6E62] flex items-center justify-center text-white text-[13px]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            H
          </div>
          <div className="leading-tight">
            <div className="text-[13px] tracking-[0.18em] uppercase text-[#2C1810]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Haven</div>
            <div className="text-[9px] tracking-[0.3em] uppercase text-[#C97070]/80" style={{ fontFamily: 'sans-serif' }}>Acne</div>
          </div>
        </a>

        <div className="hidden lg:flex items-center gap-7">
          {acneNavLinks.map(l => (
            l.href.startsWith('#') ? (
              <a key={l.label} href={l.href} className="text-[11px] tracking-[0.18em] uppercase text-[#4A3535] hover:text-[#C97070] transition-colors" style={{ fontFamily: 'sans-serif' }}>
                {l.label}
              </a>
            ) : (
              <Link key={l.label} to={l.href} className="text-[11px] tracking-[0.18em] uppercase text-[#4A3535] hover:text-[#C97070] transition-colors" style={{ fontFamily: 'sans-serif' }}>
                {l.label}
              </Link>
            )
          ))}
          <a href={JANE_APP} target="_blank" rel="noopener noreferrer" className="ml-2 px-5 py-2 rounded-full bg-[#C97070] text-white text-[10.5px] tracking-[0.2em] uppercase hover:bg-[#b85f5f] transition-all hover:shadow-[0_8px_24px_rgba(201,112,112,0.3)]" style={{ fontFamily: 'sans-serif' }}>
            Book Consultation
          </a>
        </div>

        <button onClick={() => setOpen(!open)} className="lg:hidden w-9 h-9 flex items-center justify-center text-[#2C1810]" aria-label="Toggle acne menu">
          {open ? <X className="w-4 h-4" /> : <Menu className="w-4 h-4" />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="lg:hidden overflow-hidden bg-[#FAF8F6] border-t border-[#C97070]/10">
            <div className="px-5 py-5 space-y-3">
              {acneNavLinks.map(l => (
                l.href.startsWith('#') ? (
                  <a key={l.label} href={l.href} onClick={() => setOpen(false)} className="block text-[12px] tracking-[0.2em] uppercase text-[#4A3535] py-1" style={{ fontFamily: 'sans-serif' }}>
                    {l.label}
                  </a>
                ) : (
                  <Link key={l.label} to={l.href} onClick={() => setOpen(false)} className="block text-[12px] tracking-[0.2em] uppercase text-[#4A3535] py-1" style={{ fontFamily: 'sans-serif' }}>
                    {l.label}
                  </Link>
                )
              ))}
              <a href={JANE_APP} target="_blank" rel="noopener noreferrer" className="inline-block mt-2 px-5 py-2.5 rounded-full bg-[#C97070] text-white text-[11px] tracking-[0.2em] uppercase" style={{ fontFamily: 'sans-serif' }}>
              Book Consultation
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

const fadeUp = {
  initial: { opacity: 0, y: 32 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
}

function AcneHealth() {
  return (
    <div id="top" className="min-h-screen bg-[#FAF8F6]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
      <HavenNav />
      <AcneSubNav />

      {/* ── HERO ── seamless bleed into page, no hard line */}
      <section className="relative min-h-[100svh] flex flex-col justify-end overflow-hidden pt-[60px]">
        {/* Full-bleed video background */}
        <motion.div
          className="absolute inset-0"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1.4, ease: "easeOut" }}
        >
          <video
            autoPlay
            muted
            loop
            playsInline
            poster="https://static.lumi.new/c2/c225c12bf4d6e4253d49c0f7dc94b359.webp"
            className="w-full h-full object-cover object-center"
          >
            <source src="https://static.lumi.new/material/ce/ce5b9fecf2bd12a9f3dcd3cf96d8189c.mp4" type="video/mp4" />
          </video>
          {/* Bottom fade — melts into the #FAF8F6 body */}
          <div className="absolute inset-0 bg-gradient-to-t from-[#FAF8F6] via-[#FAF8F6]/10 to-transparent" />
          {/* Subtle top nav shadow */}
          <div className="absolute inset-x-0 top-0 h-40 bg-gradient-to-b from-black/10 to-transparent" />
        </motion.div>

        {/* Hero text rides at the bottom of the image */}
        <div className="relative z-10 max-w-[1440px] mx-auto px-5 sm:px-8 lg:px-16 w-full pb-16 sm:pb-20 pt-32">
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="text-[11px] uppercase tracking-[0.32em] text-[#2C1810]/70 mb-5"
            style={{ fontFamily: "inherit" }}
          >
            Haven Acne Health
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.4 }}
            className="text-[clamp(2.8rem,7vw,6rem)] leading-[1.02] font-light text-[#2C1810] max-w-3xl"
          >
            Clear skin starts<br />with expert care.
          </motion.h1>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.7 }}
            className="mt-8 flex flex-col sm:flex-row gap-3"
          >
            <a href="https://havenintegrativehealth.janeapp.com/login" target="_blank" rel="noopener noreferrer" className="px-8 py-3.5 bg-[#C97070] text-white rounded-full text-[13px] tracking-[0.04em] hover:bg-[#b85f5f] transition-all inline-flex items-center justify-center gap-2">
              Start Your Consultation
              <ArrowRight className="w-3.5 h-3.5" />
            </a>
          </motion.div>
        </div>
      </section>

      {/* ── MARQUEE STRIP ── */}
      <div className="overflow-hidden py-5 border-y border-[#E8E2DA]">
        <motion.div
          animate={{ x: ["0%", "-50%"] }}
          transition={{ duration: 22, repeat: Infinity, ease: "linear" }}
          className="flex whitespace-nowrap"
        >
          {[...Array(2)].map((_, i) => (
            <span key={i} className="flex items-center gap-10 px-10 text-[11px] uppercase tracking-[0.28em] text-[#B8AFA6]">
              <span>No Waiting</span>
              <span className="text-[#D4A89A]">·</span>
              <span>Expert Advice</span>
              <span className="text-[#D4A89A]">·</span>
              <span>Prescription-Grade</span>
              <span className="text-[#D4A89A]">·</span>
              <span>Free Delivery</span>
              <span className="text-[#D4A89A]">·</span>
              <span>Evidence-Based Care</span>
              <span className="text-[#D4A89A]">·</span>
              <span>Pharmacist-Led</span>
              <span className="text-[#D4A89A]">·</span>
              <span>Virtual First</span>
              <span className="text-[#D4A89A]">·</span>
            </span>
          ))}
        </motion.div>
      </div>

      {/* ── FOUNDER NOTE ── Rhode-style editorial card */}
      <section id="acne-care" className="py-20 sm:py-28 lg:py-36">
        <div className="max-w-[1440px] mx-auto px-5 sm:px-8 lg:px-16">
          <div className="grid lg:grid-cols-[1.1fr_1fr] gap-10 lg:gap-20 items-start">

            {/* Left — founder note image card */}
            <motion.div
              {...fadeUp}
              transition={{ duration: 0.9 }}
            >
              <img
                src="https://static.lumi.new/a8/a89930ba0bb59a78f61283ba4ff90690.webp"
                alt="A Note From Our Founder — Dr. Saba Baig"
                className="w-full h-auto rounded-[20px] shadow-[0_30px_80px_-20px_rgba(44,24,16,0.12)]"
              />
            </motion.div>

            {/* Right — brand ethos photo + values */}
            <div className="flex flex-col gap-8">
              <motion.div
                {...fadeUp}
                transition={{ duration: 0.9, delay: 0.1 }}
              >
                <img
                  src="https://static.lumi.new/ef/ef25f59f073f7bc51af46bc2e54ab032.webp"
                  alt="Haven Acne Health — Simplicity, Evidence-Based, Prescription-Grade"
                  className="w-full h-auto rounded-[20px] shadow-[0_24px_60px_-16px_rgba(44,24,16,0.10)]"
                />
              </motion.div>

              {/* Two delivery badges */}
              <motion.div
                {...fadeUp}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="grid grid-cols-2 gap-4"
              >
                <div className="bg-[#F2EBE6] rounded-[16px] p-5 flex flex-col gap-3">
                  <Truck className="w-5 h-5 text-[#C97070]" />
                  <p className="text-[13px] font-medium text-[#2C1810] tracking-wide uppercase leading-tight" style={{ fontFamily: "sans-serif" }}>Free Delivery</p>
                  <p className="text-[13px] text-[#7A6258] leading-snug" style={{ fontFamily: "sans-serif" }}>Across the Lower Mainland</p>
                </div>
                <div className="bg-[#F2EBE6] rounded-[16px] p-5 flex flex-col gap-3">
                  <Heart className="w-5 h-5 text-[#C97070]" />
                  <p className="text-[13px] font-medium text-[#2C1810] tracking-wide uppercase leading-tight" style={{ fontFamily: "sans-serif" }}>Care Without Barriers</p>
                  <p className="text-[13px] text-[#7A6258] leading-snug" style={{ fontFamily: "sans-serif" }}>Because everyone deserves support</p>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* ── SKIN PORTRAIT + TREATMENTS ── */}
      <section id="treatments" className="py-16 sm:py-20 lg:py-28 bg-[#F2EBE6]">
        <div className="max-w-[1440px] mx-auto px-5 sm:px-8 lg:px-16">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">

            {/* Portrait */}
            <motion.div
              initial={{ opacity: 0, x: -24 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 1 }}
              viewport={{ once: true }}
              className="relative"
            >
              <img
                src="https://static.lumi.new/fd/fdcf1f9ed5593a78ca784c1b31232bac.webp"
                alt="Results — glowing, clear skin"
                className="w-full h-[420px] sm:h-[540px] lg:h-[680px] object-cover object-center rounded-[20px]"
              />
              {/* Floating label */}
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.5 }}
                viewport={{ once: true }}
                className="absolute bottom-5 left-5 bg-white/90 backdrop-blur-md px-5 py-3 rounded-2xl shadow-sm"
              >
                <p className="text-[11px] uppercase tracking-[0.22em] text-[#9B6E62]" style={{ fontFamily: "sans-serif" }}>Prescription-Grade Results</p>
              </motion.div>
            </motion.div>

            {/* Treatments list */}
            <motion.div
              {...fadeUp}
              transition={{ duration: 0.9, delay: 0.1 }}
            >
              <p className="text-[11px] uppercase tracking-[0.3em] text-[#C97070] mb-6" style={{ fontFamily: "sans-serif" }}>What We Prescribe</p>
              <h2 className="text-[clamp(2.2rem,4vw,3.8rem)] font-light text-[#2C1810] leading-[1.08] mb-10">
                Treatments that<br />actually work.
              </h2>

              {[
                {
                  name: "Retinoids",
                  detail: "Prescription-strength retinoid therapy to accelerate cell turnover, unclog pores, and reveal clearer, smoother skin."
                },
                {
                  name: "Azelaic Acid",
                  detail: "Targeted formulations to reduce inflammation, fade post-acne marks, and even skin tone."
                },
                {
                  name: "Prescription Cleansers",
                  detail: "Medical-grade cleansers designed to gently purify without stripping your skin barrier."
                }
              ].map((t, i) => (
                <motion.div
                  key={t.name}
                  initial={{ opacity: 0, x: 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: i * 0.12 }}
                  viewport={{ once: true }}
                  className="py-7 border-b border-[#DDD0C8] last:border-0"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <h3 className="text-[1.5rem] font-light text-[#2C1810] mb-2">{t.name}</h3>
                      <p className="text-[14px] text-[#7A6258] leading-relaxed" style={{ fontFamily: "sans-serif" }}>{t.detail}</p>
                    </div>
                    <span className="text-[11px] text-[#C97070] mt-1.5 shrink-0" style={{ fontFamily: "sans-serif" }}>Rx</span>
                  </div>
                </motion.div>
              ))}

              <p className="mt-8 text-[13px] text-[#9B8880] italic leading-relaxed" style={{ fontFamily: "sans-serif" }}>
                All prescriptions assessed and authorized by our licensed clinician based on your individual skin needs.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── HOW IT WORKS ── */}
      <section id="how-it-works" className="py-20 sm:py-28 lg:py-36">
        <div className="max-w-[1440px] mx-auto px-5 sm:px-8 lg:px-16">
          <motion.div {...fadeUp} transition={{ duration: 0.8 }} className="mb-16 text-center">
            <p className="text-[11px] uppercase tracking-[0.3em] text-[#C97070] mb-4" style={{ fontFamily: "sans-serif" }}>Your Journey</p>
            <h2 className="text-[clamp(2rem,4vw,3.5rem)] font-light text-[#2C1810]">How it works.</h2>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-[#E8E2DA] rounded-[20px] overflow-hidden">
            {[
              { n: "01", title: "Book Your Consultation", body: "No referral needed, no waitlist. Schedule virtually in minutes." },
              { n: "02", title: "Expert Skin Assessment", body: "Our clinician evaluates your skin and creates a personalized plan." },
              { n: "03", title: "Prescription Issued", body: "Receive tailored prescriptions for retinoids, azelaic acid, or medical-grade cleansers." },
              { n: "04", title: "Delivered to You", body: "Treatments shipped directly to your door. Free delivery depending on location." },
            ].map((item, i) => (
              <motion.div
                key={item.n}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: i * 0.1 }}
                viewport={{ once: true }}
                className="bg-[#FAF8F6] p-8 lg:p-10"
              >
                <span className="text-[3rem] font-light text-[#E8DFDA] leading-none block mb-6">{item.n}</span>
                <h3 className="text-[1.3rem] font-light text-[#2C1810] mb-3">{item.title}</h3>
                <p className="text-[14px] text-[#7A6258] leading-relaxed" style={{ fontFamily: "sans-serif" }}>{item.body}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CREDIBILITY ── */}
      <section id="why-haven" className="py-20 sm:py-24 bg-[#2C1810]">
        <div className="max-w-[1440px] mx-auto px-5 sm:px-8 lg:px-16">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <motion.div {...fadeUp} transition={{ duration: 0.9 }}>
              <p className="text-[11px] uppercase tracking-[0.3em] text-[#C97070] mb-6" style={{ fontFamily: "sans-serif" }}>Clinical Expertise</p>
              <h2 className="text-[clamp(2rem,3.5vw,3rem)] font-light text-white mb-8 leading-[1.15]">
                Pharmacist-led acne care<br />you can trust.
              </h2>
              <p className="text-[16px] text-white/70 leading-relaxed mb-10" style={{ fontFamily: "sans-serif" }}>
                Haven Acne Health is led by Dr. Saba Baig, a pharmacist with prescribing authority in BC for minor ailments, with a special interest in topical acne therapies. Your care is evidence-based, personalized, and delivered with a functional medicine approach.
              </p>
              {[
                "Prescribing authority for topical acne therapies in BC",
                "Personalized prescription skincare plans",
                "Barrier repair education and ongoing support",
                "Functional medicine approach to skin health",
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-3 mb-4">
                  <div className="w-1.5 h-1.5 bg-[#C97070] rounded-full mt-2 shrink-0" />
                  <p className="text-[15px] text-white/80" style={{ fontFamily: "sans-serif" }}>{item}</p>
                </div>
              ))}
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 1 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="rounded-[20px] overflow-hidden">
                <img
                  src="https://static.lumi.new/76/76dfba6e83c663cc75ec4145e4879a20.webp"
                  alt="Dr. Saba Baig"
                  className="w-full h-[360px] sm:h-[440px] lg:h-[520px] object-cover object-top"
                />
              </div>
              <div className="absolute bottom-5 left-5 bg-white/95 backdrop-blur-sm px-5 py-4 rounded-xl shadow">
                <p className="text-[14px] font-medium text-[#2C1810]">Dr. Saba Baig</p>
                <p className="text-[12px] text-[#9B6E62] mt-0.5" style={{ fontFamily: "sans-serif" }}>PharmD, BSc, CDE. Prescribing Authority (BC)</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── WHO IT'S FOR ── */}
      <section id="faq" className="py-20 sm:py-28 lg:py-36">
        <div className="max-w-[1440px] mx-auto px-5 sm:px-8 lg:px-16">
          <motion.div {...fadeUp} transition={{ duration: 0.8 }} className="text-center mb-16">
            <p className="text-[11px] uppercase tracking-[0.3em] text-[#C97070] mb-4" style={{ fontFamily: "sans-serif" }}>Is This For You?</p>
            <h2 className="text-[clamp(2rem,4vw,3.5rem)] font-light text-[#2C1810]">Haven Acne Health is for you if...</h2>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              "You're tired of over-the-counter products that don't work",
              "You want prescription-grade skincare without long wait times",
              "You're dealing with hormonal or persistent acne",
              "You want expert guidance on barrier repair and skin health",
            ].map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: i * 0.1 }}
                viewport={{ once: true }}
                className="border border-[#E8E2DA] rounded-[20px] p-8 text-center hover:border-[#C97070]/40 transition-colors"
              >
                <span className="text-[2.2rem] font-light text-[#E8DFDA] block mb-5 leading-none">0{i + 1}</span>
                <p className="text-[15px] text-[#4A3535] leading-relaxed" style={{ fontFamily: "sans-serif" }}>{item}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FINAL CTA ── */}
      <section className="py-20 sm:py-32 bg-[#F2EBE6]">
        <div className="max-w-[1440px] mx-auto px-5 sm:px-8 lg:px-16">
          <motion.div {...fadeUp} transition={{ duration: 0.8 }} className="max-w-2xl mx-auto text-center">
            <h2 className="text-[clamp(2.4rem,5vw,4rem)] font-light text-[#2C1810] mb-8 leading-[1.08]">
              Ready for clear,<br />confident skin?
            </h2>
            <p className="text-[16px] text-[#7A6258] mb-12 leading-relaxed" style={{ fontFamily: "sans-serif" }}>
              No waitlists. No referrals. Book your virtual consultation today and get prescription-grade skincare delivered to your door.
            </p>
            <a href="https://havenintegrativehealth.janeapp.com/login" target="_blank" rel="noopener noreferrer" className="group px-10 py-4 bg-[#C97070] text-white rounded-full text-[13px] tracking-[0.04em] hover:bg-[#b85f5f] transition-all inline-flex items-center justify-center gap-3" style={{ fontFamily: "sans-serif" }}>
              Book Your Acne Consultation
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </a>
          </motion.div>
        </div>
      </section>

      {/* ── FOOTER ── */}
      <footer className="bg-[#2C1810] text-white py-14 sm:py-16">
        <div className="max-w-[1440px] mx-auto px-5 sm:px-8 lg:px-16">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-16 mb-14">
            <div>
              <h4 className="text-[28px] mb-4">Haven</h4>
              <p className="text-[14px] text-white/50 leading-relaxed" style={{ fontFamily: "sans-serif" }}>
                Acne Health.<br />Clear skin, delivered.
              </p>
            </div>
            <div>
              <h5 className="text-[11px] uppercase tracking-[0.2em] text-white/40 mb-5" style={{ fontFamily: "sans-serif" }}>Treatments</h5>
              <ul className="space-y-3 text-[14px] text-white/60" style={{ fontFamily: "sans-serif" }}>
                <li><a href="#" className="hover:text-white transition-colors">Retinoids</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Azelaic Acid</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Prescription Cleansers</a></li>
              </ul>
            </div>
            <div>
              <h5 className="text-[11px] uppercase tracking-[0.2em] text-white/40 mb-5" style={{ fontFamily: "sans-serif" }}>Haven Health</h5>
              <ul className="space-y-3 text-[14px] text-white/60" style={{ fontFamily: "sans-serif" }}>
                <li><Link to="/" className="hover:text-white transition-colors">Home</Link></li>
                <li><Link to="/travelhealth" className="hover:text-white transition-colors">Travel Health</Link></li>
                <li><Link to="/menopausehealth" className="hover:text-white transition-colors">Menopause Health</Link></li>
                <li><Link to="/havenwellnesshealth" className="hover:text-white transition-colors">Wellness & IV Therapy</Link></li>
              </ul>
            </div>
            <div>
              <h5 className="text-[11px] uppercase tracking-[0.2em] text-white/40 mb-5" style={{ fontFamily: "sans-serif" }}>Contact</h5>
              <ul className="space-y-3 text-[14px] text-white/60" style={{ fontFamily: "sans-serif" }}>
                <li><a href="https://havenintegrativehealth.janeapp.com/login" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">Book Consultation</a></li>
                <li><Link to="/team" className="hover:text-white transition-colors">Our Team</Link></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-white/10 pt-8 flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-[13px] text-white/30" style={{ fontFamily: "sans-serif" }}>
              &copy; 2024 Haven Integrative Health Consulting Inc. All rights reserved.
            </p>
            <p className="text-[13px] text-white/30" style={{ fontFamily: "sans-serif" }}>
              Vancouver, British Columbia
            </p>
          </div>
        </div>
      </footer>
    </div>
  )
}

export default AcneHealth
